import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Sparkles, Clock, MapPin, ArrowRight } from "lucide-react";

export default function BundlesPage() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/bundles").then((r) => setBundles(r.data.bundles)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold">Multi-City Journeys</div>
          <h1 className="mt-3 text-4xl sm:text-6xl font-bold text-blue-950 dark:text-white">Package Bundles</h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300 max-w-2xl">
            Combine two or more of our signature journeys into one seamless trip — and save up to <span className="text-[#D4AF37] font-semibold">₹5,000+</span> per person.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-8">
            {[...Array(4)].map((_, i) => <div key={i} className="h-96 rounded-3xl bg-slate-100 dark:bg-slate-900 animate-pulse"/>)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {bundles.map((b) => (
              <Link key={b.slug} to={`/bundles/${b.slug}`} data-testid={`bundle-card-${b.slug}`}
                className="group rounded-3xl overflow-hidden card-lift bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 flex flex-col">
                <div className="relative h-56 overflow-hidden">
                  <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-110" style={{transition:"transform 1s ease"}} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"/>
                  {b.savings > 0 && (
                    <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-emerald-500 text-white flex items-center gap-1">
                      <Sparkles className="w-3 h-3"/> Save {format(b.savings)}
                    </div>
                  )}
                  <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold bg-[#D4AF37] text-blue-950">
                    {b.tag}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white text-2xl font-bold">{b.title}</h3>
                    <p className="text-white/80 text-sm">{b.subtitle}</p>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-4">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3"/> {b.duration}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/> {b.packages.map(p => p.location).join(" + ")}</span>
                  </div>
                  <div className="flex-1"/>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Bundle price</div>
                      <div className="text-2xl font-bold text-blue-950 dark:text-white leading-tight">{format(b.bundle_price)}</div>
                      {b.original_price > b.bundle_price && (
                        <div className="text-xs text-slate-500 line-through mt-0.5">{format(b.original_price)}</div>
                      )}
                    </div>
                    <span className="px-4 py-2 rounded-full btn-gold text-xs font-bold flex items-center gap-1">
                      View bundle <ArrowRight className="w-3 h-3"/>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
